/**
 * CRM Multi-Compagnia (Fase 2) — Componenti e hook condivisi.
 *
 * Fornisce: API client centralizzato, hook useApi, Spinner, EmptyState,
 * StatusBadge, RamoChip, CompanyAvatar, RevealCredentialsModal.
 *
 * Stile coerente con il CRM admin esistente (zinc/emerald, Tailwind, lucide).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Inbox, Lock, X, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";

export const API_BASE = process.env.REACT_APP_BACKEND_URL;
export const CRM_MULTI = `${API_BASE}/api/admin/crm/multi`;

// ============================================================================
// API client
// ============================================================================
export const api = {
  get: (path, params) => axios.get(`${CRM_MULTI}${path}`, { params, withCredentials: true }).then(r => r.data),
  post: (path, body, params) => axios.post(`${CRM_MULTI}${path}`, body, { params, withCredentials: true }).then(r => r.data),
  patch: (path, body) => axios.patch(`${CRM_MULTI}${path}`, body, { withCredentials: true }).then(r => r.data),
  del: (path) => axios.delete(`${CRM_MULTI}${path}`, { withCredentials: true }).then(r => r.data),
};

// ============================================================================
// useApi hook (fetch+loading+error+refresh)
// ============================================================================
export function useApi(fetcher, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetcher()); }
    catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, error, refresh, setData };
}

export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}

// ============================================================================
// Atoms
// ============================================================================
export const Spinner = ({ size = "w-5 h-5", label }) => (
  <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="cm-spinner">
    <Loader2 className={`${size} animate-spin`} />{label && <span>{label}</span>}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, hint, action }) => (
  <div className="text-center py-16 px-4" data-testid="cm-empty">
    <div className="w-12 h-12 rounded-full bg-zinc-100 mx-auto mb-3 flex items-center justify-center">
      <Icon className="w-6 h-6 text-zinc-400" />
    </div>
    <p className="text-sm font-medium text-zinc-700">{title}</p>
    {hint && <p className="text-xs text-zinc-500 mt-1 max-w-sm mx-auto">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorBox = ({ error }) => error ? (
  <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700 flex items-start gap-2" data-testid="cm-error">
    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{typeof error === "string" ? error : JSON.stringify(error)}</span>
  </div>
) : null;

// ============================================================================
// Status badge
// ============================================================================
const STATO_POLIZZA_STYLES = {
  bozza: "bg-zinc-100 text-zinc-700 border-zinc-200",
  attiva: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sospesa: "bg-amber-50 text-amber-700 border-amber-200",
  scaduta: "bg-rose-50 text-rose-700 border-rose-200",
  disdetta: "bg-zinc-50 text-zinc-500 border-zinc-200",
  annullata: "bg-zinc-50 text-zinc-500 border-zinc-200",
};
const STATO_INCASSO_STYLES = {
  da_incassare: "bg-amber-50 text-amber-700 border-amber-200",
  incassata: "bg-emerald-50 text-emerald-700 border-emerald-200",
  insoluta: "bg-rose-50 text-rose-700 border-rose-200",
  rimborsata: "bg-blue-50 text-blue-700 border-blue-200",
};
const STATO_SINISTRO_STYLES = {
  aperto: "bg-amber-50 text-amber-700 border-amber-200",
  in_istruttoria: "bg-blue-50 text-blue-700 border-blue-200",
  liquidato: "bg-emerald-50 text-emerald-700 border-emerald-200",
  respinto: "bg-rose-50 text-rose-700 border-rose-200",
  chiuso: "bg-zinc-100 text-zinc-700 border-zinc-200",
};
const GENERIC_STYLES = {
  attivo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  potenziale: "bg-blue-50 text-blue-700 border-blue-200",
  perso: "bg-rose-50 text-rose-700 border-rose-200",
  ex: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

export function StatusBadge({ tipo = "polizza", valore }) {
  const styles =
    tipo === "polizza" ? STATO_POLIZZA_STYLES :
    tipo === "incasso" ? STATO_INCASSO_STYLES :
    tipo === "sinistro" ? STATO_SINISTRO_STYLES :
    GENERIC_STYLES;
  const cls = styles[valore] || "bg-zinc-100 text-zinc-700 border-zinc-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`} data-testid={`cm-badge-${tipo}-${valore}`}>
      {String(valore).replace(/_/g, " ")}
    </span>
  );
}

// ============================================================================
// Ramo chip (colori distintivi per i 18 rami)
// ============================================================================
export const RAMI_LABEL = {
  rca: "RC Auto", re: "Rischi Elementari", cvt: "CVT", casa: "Casa",
  infortuni: "Infortuni", malattia: "Malattia", vita: "Vita", tcm: "TCM",
  previdenza: "Previdenza", investimento: "Investimento", azienda: "Azienda",
  rc_professionale: "RC Professionale", cauzioni: "Cauzioni",
  tutela_legale: "Tutela Legale", viaggi: "Viaggi", cyber: "Cyber",
  commercio: "Commercio", condominio: "Condominio",
};
const RAMI_COLORS = {
  rca: "bg-blue-100 text-blue-700", cvt: "bg-blue-50 text-blue-600",
  casa: "bg-amber-100 text-amber-700", re: "bg-amber-50 text-amber-600",
  vita: "bg-red-100 text-red-700", tcm: "bg-red-50 text-red-600",
  malattia: "bg-emerald-100 text-emerald-700", infortuni: "bg-emerald-50 text-emerald-600",
  previdenza: "bg-violet-100 text-violet-700", investimento: "bg-violet-50 text-violet-600",
  azienda: "bg-purple-100 text-purple-700",
  rc_professionale: "bg-indigo-100 text-indigo-700",
  cauzioni: "bg-slate-100 text-slate-700",
  tutela_legale: "bg-pink-100 text-pink-700",
  viaggi: "bg-sky-100 text-sky-700",
  cyber: "bg-cyan-100 text-cyan-700",
  commercio: "bg-orange-100 text-orange-700",
  condominio: "bg-teal-100 text-teal-700",
};
export function RamoChip({ ramo, size = "sm" }) {
  const cls = RAMI_COLORS[ramo] || "bg-zinc-100 text-zinc-700";
  const sz = size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5";
  return <span className={`inline-block rounded ${sz} font-medium ${cls}`} data-testid={`cm-ramo-${ramo}`}>{RAMI_LABEL[ramo] || ramo}</span>;
}

// ============================================================================
// Company avatar (iniziali colorate)
// ============================================================================
const COMPANY_COLORS = ["bg-emerald-600", "bg-blue-600", "bg-violet-600", "bg-amber-600", "bg-rose-600", "bg-cyan-600"];
export function CompanyAvatar({ nome, size = "md" }) {
  const initials = (nome || "?").split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const colorIdx = (nome || "").charCodeAt(0) % COMPANY_COLORS.length;
  const cls = COMPANY_COLORS[colorIdx];
  const sz = size === "sm" ? "w-7 h-7 text-[10px]" : size === "lg" ? "w-12 h-12 text-base" : "w-9 h-9 text-xs";
  return <div className={`${sz} ${cls} rounded-full text-white font-semibold flex items-center justify-center flex-shrink-0`}>{initials}</div>;
}

// ============================================================================
// Modal (drawer-like, right side, 60% width)
// ============================================================================
export function Drawer({ open, onClose, title, subtitle, actions, children, testid = "cm-drawer" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="ml-auto w-full md:w-[60%] lg:w-[55%] xl:w-[50%] h-full bg-white shadow-2xl flex flex-col relative">
        <header className="flex items-start justify-between px-6 py-4 border-b border-zinc-100">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900">{title}</h2>
            {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="cm-drawer-close"><X className="w-4 h-4" /></button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Modal centrale (per dialoghi piccoli)
// ============================================================================
export function Modal({ open, onClose, title, children, testid = "cm-modal", maxWidth = "max-w-md" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-2xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><X className="w-4 h-4" /></button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Reveal Credentials Modal — admin-only, richiede reason
// ============================================================================
export function RevealCredentialsModal({ companyId, companyNome, onClose }) {
  const [reason, setReason] = useState("");
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 10) { setError("Motivo: minimo 10 caratteri"); return; }
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/companies/${companyId}/credentials`, { reason });
      setCreds(r);
    } catch (e2) { setError(e2?.response?.data?.detail || e2.message); }
    finally { setLoading(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Credenziali compagnia · ${companyNome}`} maxWidth="max-w-lg" testid="cm-reveal-creds">
      {!creds ? (
        <form onSubmit={submit} className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
            <Lock className="w-3.5 h-3.5 inline mr-1" />
            Azione sensibile: l'accesso alle credenziali viene <strong>registrato in audit log</strong> con il motivo che dichiari.
          </div>
          <label className="block">
            <span className="text-xs font-medium text-zinc-700">Motivo dell'accesso (min. 10 caratteri)</span>
            <textarea data-testid="cm-reveal-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Es. Verifica accesso portale per emissione preventivo cliente Mario Rossi"
              className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
          </label>
          <ErrorBox error={error} />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-zinc-200 hover:bg-zinc-50">Annulla</button>
            <button type="submit" disabled={loading} data-testid="cm-reveal-submit"
              className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Rivela credenziali
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800 flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" />
            <div>Audit log scritto. Ultima rotazione: {creds.last_rotated_at ? new Date(creds.last_rotated_at).toLocaleString("it-IT") : "—"}</div>
          </div>
          {Object.entries(creds.credenziali || {}).length === 0 ? (
            <p className="text-sm text-zinc-500">Nessuna credenziale salvata per questa compagnia.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(creds.credenziali).map(([k, v]) => (
                <CredField key={k} label={k} value={v} initiallyMasked={["password", "token", "api_key", "secret", "pin"].includes(k.toLowerCase())} />
              ))}
            </div>
          )}
          <div className="flex justify-end pt-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded bg-zinc-900 text-white hover:bg-zinc-800">Chiudi</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function CredField({ label, value, initiallyMasked }) {
  const [masked, setMasked] = useState(initiallyMasked);
  return (
    <div className="flex items-center gap-2 p-2 border border-zinc-200 rounded">
      <span className="text-[10px] uppercase font-medium text-zinc-500 w-20">{label}</span>
      <code className="text-sm flex-1 font-mono text-zinc-900 select-all">
        {masked ? "•".repeat(Math.max(4, Math.min(20, (value || "").length))) : value}
      </code>
      {initiallyMasked && (
        <button onClick={() => setMasked(!masked)} className="p-1 text-zinc-400 hover:text-zinc-900" title={masked ? "Mostra" : "Nascondi"}>
          {masked ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Format helpers
// ============================================================================
export const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);
export const fmtDate = (iso) => !iso ? "—" : new Date(iso).toLocaleDateString("it-IT");
export const fmtDateTime = (iso) => !iso ? "—" : new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });

// ============================================================================
// Table primitive (sticky header + paginazione)
// ============================================================================
export function DataTable({ columns, rows, onRowClick, emptyTitle, emptyHint, testid = "cm-table" }) {
  if (!rows || rows.length === 0) {
    return <EmptyState title={emptyTitle || "Nessun risultato"} hint={emptyHint} />;
  }
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden bg-white" data-testid={testid}>
      <div className="overflow-x-auto max-h-[calc(100vh-280px)]">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0 z-10">
            <tr>{columns.map(c => (
              <th key={c.key} className="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider whitespace-nowrap">{c.label}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id || i} onClick={() => onRowClick?.(row)}
                className={`border-t border-zinc-100 ${onRowClick ? "cursor-pointer hover:bg-emerald-50/40" : ""}`}
                data-testid={`${testid}-row-${i}`}>
                {columns.map(c => (
                  <td key={c.key} className="px-4 py-2.5 text-zinc-700">
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Input / Select / Button primitives (compatti, riutilizzati nei form)
// ============================================================================
export const Field = ({ label, children, required, hint, error: err }) => (
  <label className="block">
    <span className="block text-xs font-medium text-zinc-700 mb-1">
      {label} {required && <span className="text-rose-500">*</span>}
    </span>
    {children}
    {hint && !err && <span className="block text-[11px] text-zinc-500 mt-0.5">{hint}</span>}
    {err && <span className="block text-[11px] text-rose-600 mt-0.5">{err}</span>}
  </label>
);

export const TextInput = (props) => (
  <input {...props}
    className={`w-full px-3 py-1.5 text-sm border border-zinc-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${props.className || ""}`} />
);

export const Select = ({ children, ...props }) => (
  <select {...props}
    className={`w-full px-3 py-1.5 text-sm border border-zinc-300 rounded bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${props.className || ""}`}>
    {children}
  </select>
);

export const Btn = ({ variant = "primary", size = "md", children, ...props }) => {
  const base = "inline-flex items-center gap-1.5 font-medium rounded transition disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-emerald-600 text-white hover:bg-emerald-700",
    secondary: "bg-zinc-900 text-white hover:bg-zinc-800",
    ghost: "text-zinc-700 hover:bg-zinc-100",
    outline: "border border-zinc-300 text-zinc-700 hover:bg-zinc-50",
    danger: "bg-rose-600 text-white hover:bg-rose-700",
  };
  const sizes = { sm: "px-2.5 py-1 text-xs", md: "px-3 py-1.5 text-sm", lg: "px-4 py-2 text-sm" };
  return <button {...props} className={`${base} ${variants[variant]} ${sizes[size]} ${props.className || ""}`}>{children}</button>;
};

// Toast minimal (no dipendenza esterna)
let _toastId = 0;
const _toastListeners = new Set();
export const toast = (msg, type = "info") => {
  const id = ++_toastId;
  _toastListeners.forEach(fn => fn({ id, msg, type }));
};
export function ToastContainer() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const handler = (t) => {
      setItems(prev => [...prev, t]);
      setTimeout(() => setItems(prev => prev.filter(x => x.id !== t.id)), 3500);
    };
    _toastListeners.add(handler);
    return () => _toastListeners.delete(handler);
  }, []);
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2" data-testid="cm-toast-container">
      {items.map(t => {
        const cls = t.type === "error" ? "bg-rose-600" : t.type === "success" ? "bg-emerald-600" : "bg-zinc-800";
        return <div key={t.id} className={`${cls} text-white text-sm px-4 py-2 rounded-lg shadow-lg max-w-md`}>{t.msg}</div>;
      })}
    </div>
  );
}
